/**
 * useTrades Hook
 *
 * Fetches trades from the trade_categories table for use in dropdowns and filters.
 * Supports the enhanced trade system with sections and display ordering.
 *
 * API Endpoints:
 * - GET /api/lookup/trade-categories - All trades organized by section
 * - GET /api/lookup/trade-categories/names - Just trade names (excluding section headers)
 * - GET /api/lookup/trade-categories/grouped - Trades grouped by section
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface Trade {
  id: string;
  name: string;
  slug: string;
  section: string | null;
  displayOrder: number | null;
  isSectionHeader: boolean;
}

export interface TradeSection {
  section: string;
  trades: Trade[];
}

interface TradesResponse {
  success: boolean;
  data: Trade[];
}

interface TradeNamesResponse {
  success: boolean;
  data: string[];
}

interface TradeSectionsResponse {
  success: boolean;
  data: TradeSection[];
}

/**
 * Hook to fetch all trades with full details
 * Includes section headers for display in grouped dropdowns
 *
 * @returns Trades array with id, name, slug, section, displayOrder, isSectionHeader
 */
export function useTrades() {
  return useQuery({
    queryKey: ['trades'],
    queryFn: async (): Promise<Trade[]> => {
      const response = await api.get<TradesResponse>('/api/lookup/trade-categories');
      return response.data;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - trades rarely change
    gcTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });
}

/**
 * Hook to fetch just trade names as a string array
 * Excludes section headers - only returns selectable trades
 * Useful for simple select/dropdown components
 *
 * @returns Array of trade names (no section headers)
 */
export function useTradeNames() {
  return useQuery({
    queryKey: ['trades', 'names'],
    queryFn: async (): Promise<string[]> => {
      const response = await api.get<TradeNamesResponse>('/api/lookup/trade-categories/names');
      return response.data;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - trades rarely change
    gcTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });
}

/**
 * Hook to fetch trades grouped by section
 * Useful for rendering grouped dropdowns with section headers
 *
 * @param enabled - Whether to issue the request (default: true). Pass a modal's open flag
 *                  when the consumer is a dialog that stays mounted while closed, so the
 *                  lookup isn't fetched for a dialog nobody opened.
 * @returns Array of sections, each containing an array of trades
 */
export function useTradeSections(enabled = true) {
  return useQuery({
    queryKey: ['trades', 'sections'],
    queryFn: async (): Promise<TradeSection[]> => {
      const response = await api.get<TradeSectionsResponse>('/api/lookup/trade-categories/grouped');
      return response.data;
    },
    enabled,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - trades rarely change
    gcTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });
}

/**
 * Helper: Filter out section headers from a trades array
 * Useful when you have full trades but need only selectable items
 */
export function getSelectableTrades(trades: Trade[]): Trade[] {
  return trades.filter(trade => !trade.isSectionHeader);
}

/**
 * Helper: Group trades by section (client-side)
 * Useful when you already have all trades loaded
 */
export function groupTradesBySection(trades: Trade[]): TradeSection[] {
  const sections: Record<string, Trade[]> = {};
  const sectionOrder: string[] = [];

  for (const trade of trades) {
    const sectionName = trade.section || 'Other';

    if (trade.isSectionHeader) {
      // Track section order based on header appearance
      if (!sectionOrder.includes(sectionName)) {
        sectionOrder.push(sectionName);
      }
      continue; // Don't include headers in the trade lists
    }

    if (!sections[sectionName]) {
      sections[sectionName] = [];
      if (!sectionOrder.includes(sectionName)) {
        sectionOrder.push(sectionName);
      }
    }

    sections[sectionName].push(trade);
  }

  return sectionOrder.map(section => ({
    section,
    trades: sections[section] || [],
  }));
}

/**
 * Interface for user's selected trades (from junction table)
 */
export interface UserTrade {
  id: string;
  name: string;
  description: string | null;
  iconName: string | null;
  section: string | null;
  displayOrder: number | null;
}

interface UserTradesResponse {
  success: boolean;
  data: UserTrade[];
  primaryTradeName?: string | null;
}

export interface UserTradesWithPrimary {
  primaryTradeName: string | null;
  trades: UserTrade[];
}

interface UpdateUserTradesResponse {
  success: boolean;
  data: UserTrade[];
}

/**
 * Hook to fetch a user's selected trades from the junction table
 * Uses the workflow endpoint that joins user_profile_trades with trade_categories
 *
 * @param userId - The user's ID
 * @param enabled - Whether to enable the query (default: true)
 * @returns User's trades array
 */
export function useUserTrades(userId: string | undefined, enabled = true) {
  return useQuery<UserTrade[]>({
    queryKey: ['user-trades', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      const response = await api.get<UserTradesResponse>(
        `/api/workflows/user-profiles/${userId}/trades`
      );
      return response.data;
    },
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch both the primary trade name and the additional trades for a user in
 * one round-trip. Used by ProfilePreviewModal so every caller renders the
 * same trade data regardless of what partner shape they passed in.
 */
export function useUserTradesAndPrimary(
  userId: string | undefined,
  enabled = true
) {
  return useQuery<UserTradesWithPrimary>({
    queryKey: ['user-trades-and-primary', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      const response = await api.get<UserTradesResponse>(
        `/api/workflows/user-profiles/${userId}/trades`
      );
      return {
        primaryTradeName: response.primaryTradeName ?? null,
        trades: response.data,
      };
    },
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to update a user's selected trades
 * Uses the workflow endpoint that manages the user_profile_trades junction table
 *
 * @param userId - The user's ID
 * @returns Mutation for updating user trades
 */
export function useUpdateUserTrades(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tradeIds: string[]) => {
      if (!userId) throw new Error('User ID is required');
      const response = await api.put<UpdateUserTradesResponse>(
        `/api/workflows/user-profiles/${userId}/trades`,
        { trades: tradeIds }
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['user-trades', userId] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/dashboard'] });
    },
  });
}

export default useTrades;
